<script lang="ts">
  export interface VerticalBarItem {
    label: string;
    value: number;
  }

  const AXIS_INTERVALS = 4;
  const NICE_STEP_FACTORS = [1, 2, 2.5, 4, 5, 8, 10];

  export function computeAxisMax(maxValue: number): number {
    if (maxValue <= 0) return AXIS_INTERVALS;
    const rawStep = maxValue / AXIS_INTERVALS;
    const magnitude = 10 ** Math.floor(Math.log10(rawStep));
    const step =
      NICE_STEP_FACTORS.map((factor) => factor * magnitude).find(
        (candidate) => candidate >= rawStep
      ) ?? rawStep;
    return step * AXIS_INTERVALS;
  }
</script>

<script setup lang="ts">
  import { computed } from 'vue';
  import { formatNumber } from '../model/format';

  interface Props {
    items: readonly VerticalBarItem[];
  }

  const props = defineProps<Props>();

  const axisMax = computed(() =>
    computeAxisMax(Math.max(0, ...props.items.map((item) => item.value)))
  );

  const gridlineOffsets = computed(() =>
    Array.from({ length: AXIS_INTERVALS + 1 }, (_, index) => (index / AXIS_INTERVALS) * 100)
  );

  const ariaLabel = computed(() =>
    props.items.map((item) => `${item.label}: ${formatNumber(item.value)}`).join('; ')
  );

  function barHeight(value: number): string {
    const ratio = Math.min(Math.max(value / axisMax.value, 0), 1);
    return `calc(100% * ${ratio})`;
  }
</script>

<template>
  <div
    data-testid="vertical-bars"
    role="img"
    :aria-label="ariaLabel"
    class="flex h-full flex-col"
  >
    <div class="relative min-h-0 flex-1 pt-6">
      <div
        aria-hidden="true"
        class="absolute inset-x-0 bottom-0 top-6"
      >
        <div
          v-for="offset in gridlineOffsets"
          :key="offset"
          data-testid="vertical-bars-gridline"
          class="absolute h-px w-full bg-gray-02"
          :style="{ top: `${offset}%` }"
        />
      </div>
      <div class="relative flex h-full items-end justify-center gap-3">
        <div
          v-for="item in props.items"
          :key="item.label"
          class="flex h-full w-[46px] min-w-0 shrink flex-col items-center justify-end"
        >
          <span
            data-testid="vertical-bar-value"
            class="whitespace-nowrap text-xs text-gray-07"
          >
            {{ formatNumber(item.value) }}
          </span>
          <div
            data-testid="vertical-bar"
            class="bar-grow-y mt-2 min-h-1 w-full shrink-0 rounded-lg"
            :style="{ height: barHeight(item.value), backgroundColor: 'var(--chart-1)' }"
          />
        </div>
      </div>
    </div>
    <div class="flex justify-center gap-3 pt-3">
      <span
        v-for="item in props.items"
        :key="item.label"
        data-testid="vertical-bar-label"
        class="w-[46px] min-w-0 shrink truncate text-center text-xs text-gray-03"
      >
        {{ item.label }}
      </span>
    </div>
  </div>
</template>

<style scoped>
  @media (prefers-reduced-motion: no-preference) {
    .bar-grow-y {
      transition: height 400ms ease-out;
      animation: bar-grow-y 400ms ease-out;
    }

    @keyframes bar-grow-y {
      from {
        height: 0;
      }
    }
  }
</style>
